/**
 * Tetri ↔ decimal-GEL crossings for the API layer (BOS-162).
 *
 * `formatGel` is the write edge for *display* and `parseGelToTetri` the read
 * edge for *typed input*. These three are the crossings the wire needs, and
 * they live here for the same reason those two do: five copies of the same
 * arithmetic scattered through `src/api/handlers/resources/*Mapping.ts` is how
 * "money crosses the boundary exactly once" quietly stops being true. Before
 * this file there were four identical inline copies of the tetri→string
 * conversion and three of the tetri→float one.
 */

/**
 * Tetri → the decimal echo the display layer prints, sign preserved.
 *
 * String, not a number: this is a rendering of an exact integer, and turning it
 * into a float first is the drift the integer-tetri contract exists to prevent.
 */
export const tetriToDecimalString = (tetri: number): string => {
    const rounded = Math.round(tetri);
    const sign = rounded < 0 ? '-' : '';
    const magnitude = Math.abs(rounded);
    return `${sign}${Math.trunc(magnitude / 100)}.${String(magnitude % 100).padStart(2, '0')}`;
};

/**
 * Decimal GEL → integer tetri, by digit position — never `value * 100`, which
 * is the float multiply the contract forbids (`0.29 * 100` is `28.999…`).
 *
 * The read-edge fallback: most endpoints ship a `*Tetri` companion, and this is
 * what a missing one degrades to. Degrading to `0` instead would read as a real
 * ₾0.00 amount, which is worse than a rounded one.
 */
export const tetriFromDecimal = (value: number): number => {
    if (!Number.isFinite(value)) {
        return 0;
    }
    const [whole = '0', frac = ''] = Math.abs(value).toFixed(2).split('.');
    const magnitude = Number(whole) * 100 + Number(frac.padEnd(2, '0'));
    return value < 0 ? -magnitude : magnitude;
};

/**
 * Tetri to a GEL number at a wire or number-field display edge.
 * Keep the canonical amount in tetri; do no arithmetic on this decimal.
 */
export const gelNumberOf = (tetri: number): number => Math.round(tetri) / 100;

/**
 * Prefer an integer-tetri field; fall back to parsing its decimal twin.
 *
 * The shape every read edge in this app wants: the wire ships both on most
 * endpoints, one on some, and a dropped companion has to degrade to the right
 * number rather than to zero.
 */
export const tetriOf = (tetri: unknown, decimal: unknown): number => {
    if (typeof tetri === 'number' && Number.isFinite(tetri)) {
        return Math.trunc(tetri);
    }
    if (typeof decimal === 'number' && Number.isFinite(decimal)) {
        return tetriFromDecimal(decimal);
    }
    return 0;
};
